import { useAppSelector, useAppTranslation } from "@/utils";
import {
  Button,
  Col,
  Collapse,
  Drawer,
  Flex,
  FloatButton,
  Input,
  Modal,
  Page,
  QRCode,
  Row,
  Typography,
  theme,
  usePaypingContext,
} from "@payping/web-components";
import { ArrowBlockLeft, Close, DesktopTower, DeviceMobileCamera, LightBulb } from "@payping/icons";
import { useNavigate } from "react-router-dom";
import { DashboardRoutes } from "@/router";
import { SubLayout } from "@/components/SubLayout";
import { useAsync, useAsyncFn } from "react-use";
import { KhavarmiyanehApis } from "@payping/api";
import { ComponentProps, useState } from "react";
import { buildUrl } from "@payping/utilities-shared/url";
import { closeId, ctaId, guideId, mobilelinkId } from "@/assets/googleTrackIds";
import { URLS } from "@payping/utilities-shared/consts";

const EzPayIframe = () => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const { breakpoints } = usePaypingContext();
  const { md } = breakpoints;
  const personalInfo = useAppSelector((state) => state.user.personalInfo);
  const { token } = theme.useToken();
  const { value } = useAsync(async () => KhavarmiyanehApis.iFrameWithCancellationTokenGet(), []);
  const [{ loading }, sendLink] = useAsyncFn(async () => {
    await KhavarmiyanehApis.sendNeoBankShortUrlSmsWithCancellationTokenGet();
  }, []);

  const items: ComponentProps<typeof Collapse>["items"] = [
    {
      key: 1,
      label: (
        <Typography.Text strong>{t("digitalAccount.instructionsDigitalAccount.accountOpening.title")}</Typography.Text>
      ),
      children: (
        <Flex vertical gap={16}>
          <Typography.Text type={"secondary"}>
            {t("digitalAccount.instructionsDigitalAccount.accountOpening.descriptionPart1")}
          </Typography.Text>
          <Flex gap={4}>
            <Typography.Text type={"secondary"}>
              {t("digitalAccount.instructionsDigitalAccount.accountOpening.descriptionPart2")}
            </Typography.Text>
          </Flex>
        </Flex>
      ),
      // @ts-expect-error: ant collapse items prop is missing data-attributes
      "data-google-track": guideId,
    },
    {
      key: 2,
      label: (
        <Typography.Text strong>{t("digitalAccount.instructionsDigitalAccount.receiveCard.title")}</Typography.Text>
      ),
      children: (
        <Typography.Text type={"secondary"}>
          {t("digitalAccount.instructionsDigitalAccount.receiveCard.description")}
        </Typography.Text>
      ),
      // @ts-expect-error: ant collapse items prop is missing data-attributes
      "data-google-track": guideId,
    },
    {
      key: 3,
      label: (
        <Typography.Text strong>
          {t("digitalAccount.instructionsDigitalAccount.grantAccountAccess.title")}
        </Typography.Text>
      ),
      children: (
        <Typography.Text type={"secondary"}>
          {t("digitalAccount.instructionsDigitalAccount.grantAccountAccess.description")}
        </Typography.Text>
      ),
      // @ts-expect-error: ant collapse items prop is missing data-attributes
      "data-google-track": guideId,
    },
    {
      key: 4,
      label: (
        <Typography.Text strong>
          {t("digitalAccount.instructionsDigitalAccount.possibilityOfDepositingSalesFundsDigitalAccount.title")}
        </Typography.Text>
      ),
      children: (
        <Typography.Text type={"secondary"}>
          {t("digitalAccount.instructionsDigitalAccount.possibilityOfDepositingSalesFundsDigitalAccount.description")}
        </Typography.Text>
      ),
      // @ts-expect-error: ant collapse items prop is missing data-attributes
      "data-google-track": guideId,
    },
  ];
  return (
    <Drawer
      title={
        <Flex gap={16}>
          <Typography.Text>{t("digitalAccount.jointAccount.title")}</Typography.Text>
          <Typography.Text type={"secondary"}>{t("settlement.bankName.Khavarmiane")}</Typography.Text>
        </Flex>
      }
      open
      placement={"bottom"}
      size={"100%"}
      closeIcon={false}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            navigate(DashboardRoutes.digitalAccount.yourAccount.url);
          }}
          data-google-track={closeId}
        />
      }
    >
      <Page>
        <SubLayout>
          <Row gutter={56}>
            <Col xs={24} lg={12} className="tw:flex! tw:justify-center!">
              {md ? (
                <Flex vertical gap={16} className="tw:h-[calc(100svh - 200px)]">
                  <iframe allow="camera;microphone" height={"100%"} src={value?.redirectURL} />
                  <Flex gap={16} className={"justify-between"}>
                    <Typography.Text>{t("digitalAccount.sendLinkMobileIframe")}</Typography.Text>
                    <Button
                      onClick={() => setOpenModal(true)}
                      className={"tw:pt-0!"}
                      type={"link"}
                      data-google-track={mobilelinkId}
                    >
                      {t("digitalAccount.jointAccount.sendLink")}
                    </Button>
                  </Flex>
                </Flex>
              ) : (
                <iframe allow="camera;microphone" className="tw:h-[calc(90svh - 200px)]" src={value?.redirectURL} />
              )}
            </Col>
            {md ? (
              <Col
                xs={24}
                lg={12}
                className={"background--gray100 tw:sticky! tw:top-px tw:flex! tw:flex-col! tw:justify-center! tw:p-8!"}
              >
                <Flex vertical gap={16} justify={"center"} align={"center"}>
                  <LightBulb width={54} strokeWidth={"0.7"} />
                  <Typography.Title level={5}>{t("digitalAccount.instructionsDigitalAccount.title")}</Typography.Title>
                </Flex>

                <Collapse
                  defaultActiveKey={[1, 2, 3, 4]}
                  expandIconPlacement={"end"}
                  items={items}
                  ghost
                  size={"middle"}
                />
              </Col>
            ) : (
              <Col xs={24}>
                <FloatButton
                  onClick={() => setOpenDrawer(true)}
                  className="tw:flex! tw:items-center! tw:justify-start! tw:p-0.75!"
                  shape={"square"}
                  description={
                    <Flex gap={8}>
                      <LightBulb width={24} />
                      <Typography.Text className={"tw:flex! tw:items-center!"}>
                        {t("digitalAccount.instructionsDigitalAccount.helpForUsingAJointAccount")}
                      </Typography.Text>
                    </Flex>
                  }
                  style={{ width: "unset" }}
                />
                <Drawer
                  title={
                    <Typography.Text>
                      {t("digitalAccount.instructionsDigitalAccount.helpForUsingAJointAccount")}
                    </Typography.Text>
                  }
                  open={openDrawer}
                  closeIcon={false}
                  placement={"bottom"}
                  size={"100%"}
                  extra={
                    <Button
                      type={"text"}
                      icon={<Close width={14} height={14} />}
                      onClick={() => {
                        setOpenDrawer(false);
                      }}
                      data-google-track={closeId}
                    />
                  }
                >
                  <Collapse defaultActiveKey={"1"} expandIconPlacement={"end"} items={items} ghost size={"middle"} />
                </Drawer>
              </Col>
            )}
          </Row>
        </SubLayout>
        <Modal
          open={openModal}
          title={t("digitalAccount.jointAccount.sendLink")}
          footer={false}
          onCancel={() => setOpenModal(false)}
          mask={{ closable: false }}
        >
          <Flex vertical gap={16}>
            <div className={"background--gray100 tw:flex! tw:justify-center! tw:p-4!"}>
              <Flex vertical gap={16}>
                <div className="tw:flex! tw:justify-center!">
                  <QRCode
                    value={buildUrl(URLS.PPNG_URL, "/t/khavarmiyanehNeoBank").toString()}
                    className="tw:flex! tw:justify-center!"
                    size={82}
                    bgColor={`${token.colorBgBase}`}
                  />
                </div>
                <Flex gap={16}>
                  <DesktopTower className={"color--cta"} width={24} />
                  <ArrowBlockLeft className={"color--cta"} width={16} />
                  <DeviceMobileCamera className={"color--cta"} width={24} />
                </Flex>
              </Flex>
            </div>
            <Typography.Title className={"tw:m-0!"} level={5}>
              {t("digitalAccount.jointAccount.betterExperienceOnMobile")}
            </Typography.Title>
            <Typography.Text type={"secondary"}>
              {t("digitalAccount.jointAccount.betterExperienceOnMobileDesc")}
            </Typography.Text>
            <Button
              type={"link"}
              href={buildUrl(URLS.PPNG_URL, "/t/khavarmiyanehNeoBank").toString()}
              className={"tw:flex! tw:justify-center!"}
            >
              {buildUrl(URLS.PPNG_URL, "/t/khavarmiyanehNeoBank").toString()}
            </Button>
            <Typography.Text type={"secondary"}>{t("digitalAccount.jointAccount.sendLinkMobileDesc")}</Typography.Text>
            <Flex gap={8}>
              <Input disabled placeholder={personalInfo?.phoneNumber} />
              <Button loading={loading} type={"primary"} onClick={() => sendLink()} data-google-track={ctaId}>
                {t("send")}
              </Button>
            </Flex>
          </Flex>
        </Modal>
      </Page>
    </Drawer>
  );
};

export default EzPayIframe;
